
import React, { useState } from 'react'

import { EllipsisVerticalIcon, ChevronDownIcon } from 'lucide-react'

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import type { MenuProps, SxProps, Theme } from '@mui/material'
import { Divider, Stack, Typography, useTheme, alpha } from '@mui/material'
import { Button, MENU_PAPER_SX, MENU_ITEM_SX } from '@repo/ui'

import {
  HOVER_MENU_PAPER_SX,
  HOVER_MENU_ROOT_SX,
  useHoverIntent,
  type HoverIntentSurfaceProps,
} from 'hooks/useHoverIntent'
import { useChromeSettledPosition } from 'hooks/useChromeSettledPosition'

/**
 * Below the trigger, right edges aligned — what every call site (mostly table
 * action columns) has always got and still gets when the placement props below
 * are omitted. Named so the default is visible rather than implied.
 */
const DEFAULT_ANCHOR_ORIGIN: MenuProps['anchorOrigin'] = { horizontal: 'right', vertical: 'bottom' }
const DEFAULT_TRANSFORM_ORIGIN: MenuProps['transformOrigin'] = { horizontal: 'right', vertical: 'top' }

interface ActionDropdownProps {
  items: ActionDropdownItem[]
  label?: string,
  icon?: React.ReactNode
  /**
   * Render a custom trigger instead of the built-in labelled button / ellipsis
   * IconButton. Receives the click handler and the open state so the caller can
   * style its own affordance (e.g. a sidebar row) while still getting this
   * component's menu — one menu implementation, many triggers.
   *
   * Added VRG-224 for the sidebar Help popover: neither built-in trigger can
   * look like a sidebar list row, and duplicating the menu styling to get one
   * is exactly the drift working-conventions §36 exists to prevent.
   *
   * Takes precedence over `variant` — a custom trigger replaces the built-ins
   * entirely, so the two are never both in play.
   *
   * `hoverProps` is empty unless `hoverIntent` is set; spread it onto the SAME element
   * that takes `onClick`, so the menu's anchor and its hover region are one thing.
   */
  trigger?: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void
    open: boolean
    hoverProps: Partial<HoverIntentSurfaceProps>
  }) => React.ReactNode
  /**
   * Opt in to hover-with-intent opening IN ADDITION to click — the pointer must rest on
   * the trigger before the menu opens, and trigger + menu count as one hover region so
   * moving between them does not dismiss it. See `useHoverIntent` for the delays and
   * for the modal-backdrop trap this has to work around.
   *
   * OFF by default, and deliberately so: this component backs every kebab in the app,
   * and a table row's action menu opening because the cursor crossed it would be
   * unusable. Only a trigger the user parks on — a sidebar row — should set it.
   */
  hoverIntent?: boolean
  /**
   * 'icon' (default): plain unbordered icon button — used in table pinned-right
   * action columns (the overwhelming majority of call sites), where a bordered
   * button on every row would be visual noise.
   * 'button': bordered secondary-button chrome matching a page header's other
   * action buttons (e.g. CompanyPage's "Go into space" / "Suspend") — use ONLY
   * when the kebab sits inline in a topbar row of buttons, not inside a table.
   */
  variant?: 'icon' | 'button'
  /**
   * Menu placement, for the rare trigger that cannot use the default drop-below.
   * OPTIONAL and additive: omit them and the menu lands exactly where it always
   * has, so no existing call site moves.
   *
   * Added VRG-248 for the sidebar Help row, which — when the sidebar is collapsed
   * to 80px — must open to the RIGHT of the drawer rather than over it.
   */
  anchorOrigin?: MenuProps['anchorOrigin']
  transformOrigin?: MenuProps['transformOrigin']
  /**
   * Merged onto the menu paper AFTER the defaults, so it can override them — e.g.
   * trading `MENU_PAPER_SX`'s 8px downward drop for a horizontal gap when opening
   * sideways. The AFTER is load-bearing: `SIDEBAR_MENU_PAPER_SX`'s `mt: 0` only wins
   * because it is layered later in the array below.
   */
  menuPaperSx?: SxProps<Theme>
}

export type ActionDropdownItem = {
  label?: string
  disabled?: boolean
  /** Shown on hover — typically why the item is disabled (e.g. a missing permission). */
  tooltip?: React.ReactNode
  dev?: boolean
  visible?: boolean
  icon?: React.ReactNode
  /**
   * Optional trailing hint, right-aligned in the row — a keyboard chord chip
   * ("⌘K", "?") for menu items that duplicate a global shortcut.
   *
   * OPTIONAL and purely additive: omit it and the row renders byte-identically
   * to before, which matters because this component backs every kebab in the
   * app. The hint is a NODE, not a string, so the caller supplies the shared
   * KeyChip and this component never grows its own chip styling.
   */
  hint?: React.ReactNode
  divider?: boolean
  destructive?: boolean
  onClick: () => void
}

export const ActionDropdown = (props: ActionDropdownProps) => {
  const {
    items,
    label,
    icon = null,
    trigger,
    variant = 'icon',
    anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
    transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
    menuPaperSx,
    hoverIntent = false,
  } = props

  /*
    Two state paths, and the click-only one is byte-identical to what it always was.

    The hook is called unconditionally (hooks rules) but `enabled: hoverIntent` keeps it
    inert, and every hover-specific prop below is gated on `hoverIntent`. That matters
    more here than in a one-off component: ~100 kebabs render through this file, and none
    of them should move because a sidebar row wanted hover.
  */
  const hover = useHoverIntent({ enabled: hoverIntent })
  const [clickAnchorEl, setClickAnchorEl] = useState<null | HTMLElement>(null)

  const anchorEl = hoverIntent ? hover.anchorEl : clickAnchorEl
  const open = Boolean(anchorEl)

  /*
    Reposition once the app chrome's width has SETTLED.

    Mostly this matters for the sidebar's Help popover, which is an ActionDropdown
    anchored to a row inside the drawer: the drawer's width is a 225ms transition and
    the hover that opens the menu is the hover that starts it, so MUI's one-shot
    `getBoundingClientRect()` at open can measure a width that no longer exists.
    Measured 206px between the two outcomes on the sibling profile menu.

    Harmless and mildly useful everywhere else — a table kebab left open while the
    sidebar expands currently drifts out of register with its trigger for the same
    reason, and now does not. See `useChromeSettledPosition`.
  */
  const menuPosition = useChromeSettledPosition(open)

  const theme = useTheme()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hoverIntent) {
      hover.handleTriggerClick(event)
      return
    }
    setClickAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    if (hoverIntent) {
      // `closeAndRestoreFocus`, not `closeNow`: this is the Menu's `onClose`, and it is
      // the ONLY callback that fires when MUI handles Escape itself — which it does
      // whenever focus is inside the menu, stopping propagation so no document-level
      // listener can see it. Returning focus to the trigger has to happen here.
      hover.closeAndRestoreFocus()
      return
    }
    setClickAnchorEl(null)
  }

  const visibleItems = items.filter(action => action.visible !== false)

  return (
    <>
      {visibleItems.length > 0 && (
        <>
          {trigger ? (
            trigger({ onClick: handleClick, open, hoverProps: hoverIntent ? hover.triggerProps : {} })
          ) : label ? (
            <LoadingButton
              sx={{
                height: '40px',
                minWidth: '118px',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'semibold',
                color: 'black',
                borderColor: 'black',
                '& .MuiLoadingButton-endIcon': {
                  color: 'black',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out'
                }
              }}
              key={`action-button-${label}`}
              id="action-button"
              aria-controls={open ? 'action-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              variant={"outlined"}
              disableElevation
              onClick={handleClick}
              startIcon={icon}
              endIcon={<ChevronDownIcon strokeWidth={1} />}
            >
              {label}
            </LoadingButton>
          ) : variant === 'button' ? (
            // Same secondary-button chrome (border + 40px height) as the other
            // header actions it sits next to (e.g. "Go into space" / "Suspend"),
            // just squared off and icon-only instead of carrying a label.
            <Tooltip title="More actions" placement="top">
              <Button
                key={`action-button-ellipsised-${label}`}
                id="action-button"
                aria-label="More actions"
                aria-controls={open ? 'action-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="secondary"
                sx={{
                  minWidth: '40px',
                  width: '40px',
                  // BOTH lines are load-bearing, and a bare `padding: 0` here was NOT enough.
                  //
                  // The Button's own rule sets `padding: 0 16px` at a specificity a plain sx
                  // value does not beat, so the 40px square kept a 32px horizontal inset and
                  // its content box collapsed to 8px. The glyph then flex-shrank to fit it:
                  // measured live at 6px wide x 24 tall — a squashed ellipsis, not the icon
                  // anyone wrote. `&.MuiButton-root` raises specificity enough for the reset
                  // to actually land; `flexShrink: 0` is the belt-and-braces half, so the
                  // glyph holds its 24px even if some future rule re-adds padding here.
                  '&.MuiButton-root': { padding: 0 },
                  '& svg': { flexShrink: 0 },
                }}
              >
                {/* No `size` override. It carried `size={18}` while every sibling in a header
                    action group renders its lucide glyph at the default 24, so the kebab read
                    visibly smaller than the buttons beside it — the one control in the row that
                    looked shrunken. */}
                <EllipsisVerticalIcon strokeWidth={1} />
              </Button>
            </Tooltip>
          ) : (
            <IconButton
              key={`action-button-ellipsised-${label}`}
              id="action-button"
              aria-controls={open ? 'action-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              sx={{
                borderRadius: 2,
              }}
            >
              <EllipsisVerticalIcon strokeWidth={1} />
            </IconButton>
          )}
          <Menu
            key={`action-item-menu-${label}`}
            action={menuPosition}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            /*
              HOVER-ONLY MODAL PROPS. All four are inert unless `hoverIntent` is set, so
              the click-only path keeps MUI's defaults exactly.

              The three `disable*` are about FOCUS, and they are gated on HOW the menu
              opened rather than being always-on. A menu that opened because the pointer
              drifted must not drag the caret out of whatever the user was doing, and
              MUI's FocusTrap re-checks on an interval, so merely not auto-focusing is
              not enough — hence suppressing the lot for a pointer open.

              But a KEYBOARD open wants the opposite, and suppressing them there was a
              real regression, caught in verification: with `disableAutoFocus` on, focus
              never entered the menu at all, so the arrow keys — which MUI handles on the
              MenuList — had nothing to act on. Letting MUI's defaults stand for the
              keyboard case restores exactly the behaviour this component had before
              hover existed, instead of approximating it with `autoFocus` alone.

              `disableScrollLock` is not about focus and stays unconditional: a
              hover-opened menu would otherwise lock body scroll — and pay its scrollbar
              compensation, shifting the page — every time the cursor rested on a row.
            */
            /*
              ONLY `disableAutoFocus` is gated on how the menu opened; the other two are
              unconditional. Each of the three was measured, and the asymmetry is real:

              - AUTO: must be OFF for a keyboard open, or focus never enters the menu and
                the arrow keys (which MUI handles on the MenuList) have nothing to act
                on. Must be ON for a pointer open, or a menu that appeared because the
                cursor drifted yanks the caret out of whatever the user was doing.
              - ENFORCE: always suppressed. MUI's FocusTrap re-checks on an interval and
                drags focus back inside, which defeats the restore below — and a menu
                does not need a trap, since it is arrow-key navigable and Tab leaving it
                is reasonable.
              - RESTORE: always suppressed, because there must be exactly ONE actor and
                MUI cannot be it. It records the focused node at MOUNT, by which point
                MenuList's `autoFocus` has already moved focus onto the first item, so it
                restores to a menu item that is being unmounted and focus lands on
                <body>. Measured: Escape left the keyboard user on <body>, outside the
                tab order. `useHoverIntent` restores to the trigger instead.
            */
            disableAutoFocus={hoverIntent && !hover.openedByKeyboard}
            disableEnforceFocus={hoverIntent}
            disableRestoreFocus={hoverIntent}
            disableScrollLock={hoverIntent}
            autoFocus={hoverIntent ? hover.openedByKeyboard : undefined}
            MenuListProps={{
              'aria-labelledby': 'action-button',
              sx: {
                minWidth: '200px',
                // Horizontal inset now lives on the paper (MENU_PAPER_SX below) — the
                // house convention shared by every menu in the app.
                pt: 0,
                pb: 0,
              },
            }}
            slotProps={{
              // The invisible modal root is switched OFF for pointer purposes — see note
              // 3 in `useHoverIntent`. Without it the root covers the trigger the instant
              // the menu opens, the trigger loses hover, and the menu closes itself while
              // the pointer has not moved.
              ...(hoverIntent ? { root: { sx: HOVER_MENU_ROOT_SX } } : {}),
              paper: {
                // ...and back on for the paper, which does need to be hovered and
                // clicked. The paper also joins the trigger's hover region, so travelling
                // from one to the other never dismisses the menu.
                ...(hoverIntent ? hover.surfaceProps : {}),
                // Array form (not a spread) so `menuPaperSx` may be any valid sx —
                // object, array, or theme callback — and still layer over the defaults.
                sx: [
                  {
                    borderRadius: 2,
                    boxShadow: `rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px`,
                    border: `1px solid ${theme.palette.primary[100]}`,
                    ...MENU_PAPER_SX,
                  },
                  ...(hoverIntent ? [HOVER_MENU_PAPER_SX] : []),
                  ...(Array.isArray(menuPaperSx) ? menuPaperSx : menuPaperSx ? [menuPaperSx] : []),
                ],
              },
            }}
            transformOrigin={transformOrigin}
            anchorOrigin={anchorOrigin}
          >
            {visibleItems.map((item, index) => {
              const menuItem = (
                <MenuItem
                  key={`menuitem-${index}`}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  sx={{
                    ...MENU_ITEM_SX,
                    // Fill the paper so `ml:auto` on the hint actually reaches the right
                    // edge. Without it each MenuItem shrink-wraps its own content and the
                    // chips land at different x per row — 68px vs 27px from the edge.
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    background: item.dev && alpha(theme.palette.brand[500], .1),
                    color: item.dev && theme.palette.brand[500],
                    minHeight: '44px',
                    /*
                      A hovered row BRIGHTENS; it must never change weight.

                      Weight is pinned rather than merely left alone. No rule in this
                      repo sets `fontWeight` on hover — grepped across `packages/ui` and
                      `apps/platform`, there is none — yet the rows visibly thicken and
                      shift under the cursor. Whatever supplies it (a UA/synthetic-bold
                      pass over text that has just gained a background is the likeliest
                      candidate), pinning the value makes the row immune to it without
                      needing to win that argument. A menu whose text reflows under the
                      pointer is the one interaction where the target moves as you aim
                      at it.

                      Brightening to `text.primary` is what carries the hover instead. It
                      is inert wherever a row already renders at full contrast, so this
                      cannot dim or lift any existing menu — it only closes the gap on
                      rows that sit at secondary.
                    */
                    '& .MuiTypography-root': {
                      fontWeight: 400,
                      transition: 'color 0.12s ease',
                    },
                    '&:hover': {
                      backgroundColor: item.destructive ? `rgba(239, 68, 68, 0.04)` : 'default',
                      background: item.dev && alpha(theme.palette.brand[500], .2),
                      '& .MuiTypography-root': {
                        fontWeight: 400,
                        color: item.destructive ? theme.palette.error.main : theme.palette.text.primary,
                      },
                    }
                  }} >
                  {/* 12px between the leading icon and its label (§29 on the 4px
                      base). Was 1.5 (6px), which read as the glyph crowding the
                      word. APP-WIDE by construction: every kebab / overflow menu
                      in the platform renders through this component. */}
                  <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
                    <div style={{
                      color: item.destructive ? theme.palette.error.main : 'default',
                      display: 'flex',
                      alignItems: 'center'
                    }}>{item.icon}</div>
                    {/* flexGrow on the LABEL, not margin on the hint. MUI Stack emits
                        `& > :not(style):not(style) { margin-left: <spacing> }`, which
                        out-specifies an sx class — so `ml: auto` on the hint silently
                        computed to 12px and every chip sat wherever its label ended
                        (measured 51px vs 20px from the paper edge). Growing the label
                        instead pushes the hint to the right edge and cannot be beaten
                        by the Stack reset. Rows with no hint are unaffected. */}
                    <Typography
                      variant="body2"
                      color={item.destructive ? 'error' : 'default'}
                      sx={{ flexGrow: 1 }}
                    >
                      {item.label}
                    </Typography>
                    {/* ml:auto rather than justifyContent:space-between on the Stack:
                        space-between would also push the icon away from the label on
                        every hint-less row in the app. This way the hint is the only
                        thing that moves, and rows without one are untouched. */}
                    {item.hint && <Box sx={{ display: 'flex', alignItems: 'center' }}>{item.hint}</Box>}
                  </Stack>
                </MenuItem>
              )
              return (
                <React.Fragment key={`action-item-${index}`}>
                  {item.divider && index > 0 && (<Divider key={`divider-${index}`} flexItem />)}
                  {item.tooltip ? (
                    // Disabled items emit no hover events, so a span gives the
                    // tooltip a target (typically explaining a missing permission).
                    <Tooltip title={item.tooltip} arrow placement="left">
                      {item.disabled ? <span style={{ display: 'block' }}>{menuItem}</span> : menuItem}
                    </Tooltip>
                  ) : menuItem}
                </React.Fragment>
              )
            })}
          </Menu>
        </>
      )}
    </>
  )
}
